(() => {
  if (globalThis.JaxStudioAnalytics) return;

  const actions = new Set(['loaded', 'design_started', 'product_change', 'variant_change', 'text_added', 'logo_upload_started', 'logo_upload_success', 'logo_upload_error', 'library_select', 'preview_opened', 'review_opened', 'review_confirmed', 'cart_attempt', 'cart_success', 'cart_error', 'design_saved', 'design_loaded', 'design_downloaded', 'undo', 'redo', 'layer_removed', 'reset', 'exit_summary']);
  const enums = {
    file_type: new Set(['png', 'jpg', 'jpeg', 'svg', 'pdf', 'ai', 'other']),
    file_size_bucket: new Set(['small', 'medium', 'large']),
    error_code: new Set(['validation', 'unavailable', 'upload_failed', 'cart_failed', 'cart_uncertain', 'export_failed', 'design_invalid']),
    tool: new Set(['text', 'monogram', 'upload', 'template', 'graphic', 'preview', 'review', 'cart', 'repeat', 'download', 'history', 'selection', 'design']),
  };
  const maxima = { duration_ms: 86400000, layer_count: 100, text_count: 100, logo_count: 100, side_count: 2, action_count: 10000 };
  const id = value => {
    const candidate = String(value ?? '');
    return /^\d{1,20}$/.test(candidate) ? candidate : null;
  };
  const clean = source => {
    const result = {};
    if (!source || typeof source !== 'object') return result;
    for (const [key, limit] of Object.entries(maxima)) {
      if (Number.isFinite(source[key]) && source[key] >= 0) result[key] = Math.min(limit, Math.floor(source[key]));
    }
    for (const [key, allowed] of Object.entries(enums)) if (allowed.has(source[key])) result[key] = source[key];
    if (typeof source.reviewed === 'boolean') result.reviewed = source.reviewed;
    return result;
  };
  const mode = () => {
    const query = new URLSearchParams(globalThis.location?.search || '');
    const test = query.get('je_analytics_test') === '1';
    const role = globalThis.Shopify?.theme?.role;
    return { test, preview: Boolean(globalThis.Shopify?.designMode || query.has('preview_theme_id') || query.has('_fd') || role && role !== 'main') };
  };
  const allowed = () => {
    try {
      const current = mode();
      return globalThis.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.() === true && (!current.preview || current.test);
    } catch { return false; }
  };
  const publish = (name, payload) => {
    try {
      if (!allowed() || typeof globalThis.Shopify?.analytics?.publish !== 'function') return false;
      const result = globalThis.Shopify.analytics.publish(name, { ...payload, ...(mode().test ? { isTest: true } : {}) });
      Promise.resolve(result).catch(() => {});
      return true;
    } catch { return false; }
  };
  const fileMetadata = file => {
    try {
      const extension = String(file?.name || '').split('.').pop().toLowerCase();
      const size = Number(file?.size);
      return {
        file_type: enums.file_type.has(extension) ? extension : 'other',
        ...(Number.isFinite(size) && size >= 0 ? { file_size_bucket: size < 1048576 ? 'small' : size <= 5242880 ? 'medium' : 'large' } : {}),
      };
    } catch { return {}; }
  };

  const create = ({ context = () => ({}), disabled = false } = {}) => {
    let usable = false, loaded = false, started = false, closed = false, exited = false, actionCount = 0;
    let began = 0, flowId = null;
    const now = () => globalThis.performance?.now?.() ?? Date.now();
    const emit = (action, extra, identity) => {
      try {
        if (disabled || closed || !flowId || !allowed()) return;
        const state = context() || {};
        const payload = {
          productId: id(identity?.productId ?? state.productId), variantId: id(identity?.variantId ?? state.variantId), action,
          extra: { ...clean(state), ...clean(extra), studio: true, flow_id: flowId, duration_ms: Math.min(86400000, Math.max(0, Math.floor(now() - began))), action_count: actionCount },
        };
        publish('studio_action', payload);
      } catch {}
    };
    const activate = () => {
      if (!usable || disabled || closed || !allowed()) return false;
      if (!loaded) {
        flowId = globalThis.crypto?.randomUUID?.();
        if (!/^[\da-f]{8}-(?:[\da-f]{4}-){3}[\da-f]{12}$/i.test(flowId || '')) return false;
        began = now(); loaded = true;
        emit('loaded');
      }
      return true;
    };
    const start = () => {
      try {
        if (!activate() || started) return;
        started = true;
        emit('design_started');
      } catch {}
    };
    const track = (action, extra = {}, identity) => {
      try {
        if (closed || !actions.has(action)) return;
        if (action === 'loaded') {
          usable = true; activate(); return;
        }
        if (!activate()) return;
        if (action === 'design_started') { start(); return; }
        if (action === 'exit_summary') {
          if (exited) return;
          exited = true;
        } else actionCount = Math.min(10000, actionCount + 1);
        emit(action, extra, identity);
      } catch {}
    };
    const exit = () => track('exit_summary');
    const newDesign = () => {
      if (!usable || closed) return;
      exit(); loaded = false; started = false; exited = false; actionCount = 0;
      track('loaded');
    };
    const consent = () => {
      try {
        if (!allowed()) { loaded = false; started = false; exited = false; actionCount = 0; flowId = null; }
        else activate();
      } catch {}
    };
    const pagehide = event => { if (!event.persisted) exit(); };
    try {
      globalThis.addEventListener?.('pagehide', pagehide);
      globalThis.document?.addEventListener?.('visitorConsentCollected', consent);
      globalThis.document?.addEventListener?.('customerPrivacyApiLoaded', consent);
    } catch {}
    return Object.freeze({
      track, start, exit, newDesign,
      dispose() {
        exit(); closed = true;
        try {
          globalThis.removeEventListener?.('pagehide', pagehide);
          globalThis.document?.removeEventListener?.('visitorConsentCollected', consent);
          globalThis.document?.removeEventListener?.('customerPrivacyApiLoaded', consent);
        } catch {}
      },
    });
  };
  globalThis.JaxStudioAnalytics = Object.freeze({ create, fileMetadata, allowed, publish });

  let privacyRequested = false;
  const loadPrivacy = () => {
    try {
      if (typeof globalThis.Shopify?.customerPrivacy?.analyticsProcessingAllowed === 'function' || privacyRequested) return true;
      if (typeof globalThis.Shopify?.loadFeatures !== 'function') return false;
      privacyRequested = true;
      let settled = false;
      globalThis.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], error => {
        if (settled) return;
        settled = true;
        try {
          if (!error && typeof globalThis.Shopify?.customerPrivacy?.analyticsProcessingAllowed === 'function') {
            globalThis.document?.dispatchEvent?.(new Event('customerPrivacyApiLoaded'));
          }
        } catch {}
      });
      return true;
    } catch { return true; }
  };
  if (!loadPrivacy() && globalThis.document?.readyState === 'loading') {
    globalThis.document.addEventListener('DOMContentLoaded', loadPrivacy, { once: true });
  }
})();
